import { memo, useCallback, useMemo } from "react";

// Helpers
import { classNames } from "helpers";

// UI
import { CardSelect } from "ui/components/CardSelect";
import { CardSelectOption } from "ui/components/CardSelect/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import OfficeDropdown from "ui/components/Dropdown/OfficeDropdown/OfficeDropdown";
import { TextField } from "ui/components/TextField/TextField";
import { DatePicker } from "ui/components/DatePicker";

// Servisni sloj
import {
  ALL_VEHICLE_LOCATION_TYPES,
  VEHICLE_LOCATION_TYPE_CONFIG,
  locationRequiresAddress,
  locationRequiresContact,
  locationRequiresOffice,
} from "services/api/fleet/vehicles/locations/consts";
import { VehicleLocationTypeEnum } from "services/api/fleet/vehicles/locations/types";

import { VehicleLocationFieldsProps } from "./types";

/**
 * Polja lokacije vozila. Koriste ih i premještaj i modal detalja statusa, da pravila
 * ("na servisu traži servis, poslovnica traži poslovnicu") budu ista u oba toka.
 */
const VehicleLocationFields: React.FC<VehicleLocationFieldsProps> = (props) => {
  const {
    values,
    errors,
    onChange,
    showValidFrom = true,
    showNote = true,
    layout = "stack",
    hideContact = false,
    disabled,
  } = props;

  const config = VEHICLE_LOCATION_TYPE_CONFIG[values.locationType];

  // U mreži nositelj lokacije ide preko cijelog retka — jedna kolona je preuska za naziv.
  const fullWidthClass = layout === "grid" ? "col-span-3" : "";

  // Adresa i napomena su duži unosi, pa uzimaju dvije kolone i popunjavaju redak
  // uz mjesto odnosno datum premještaja.
  const wideClass = layout === "grid" ? "col-span-2" : "";

  // Opis stane samo u širokom rasporedu; u modalu bi kartice postale previsoke.
  const typeOptions: CardSelectOption[] = useMemo(
    () =>
      ALL_VEHICLE_LOCATION_TYPES.map((type) => ({
        id: type,
        icon: VEHICLE_LOCATION_TYPE_CONFIG[type].icon,
        title: VEHICLE_LOCATION_TYPE_CONFIG[type].label,
        description:
          layout === "grid"
            ? VEHICLE_LOCATION_TYPE_CONFIG[type].description
            : undefined,
      })),
    [layout]
  );

  // Promjena vrste briše nositelja prethodne vrste — inače bi ostao zaostali kontakt
  // uz lokaciju koja ga ne prikazuje.
  const handleTypeChange = useCallback(
    (id: string) => {
      onChange({
        ...values,
        locationType: id as VehicleLocationTypeEnum,
        officeId: null,
        contactId: null,
        addressLine1: null,
        addressCity: null,
        addressPostalCode: null,
        addressCountry: null,
      });
    },
    [values, onChange]
  );

  const handleOfficeChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onChange({ ...values, officeId: item.value || null });
    },
    [values, onChange]
  );

  const handleContactChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      onChange({ ...values, contactId: item.value || null });
    },
    [values, onChange]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      onChange({ ...values, [name]: value });
    },
    [values, onChange]
  );

  return (
    <div
      className={classNames(
        layout === "grid" ? "grid grid-cols-3 gap-3" : "flex flex-col gap-3"
      )}
    >
      {/* Izbornik vrste ide preko cijele širine — nositelj lokacije ispod njega ovisi o odabiru.
          CardSelect nema disabled, pa se zaključavanje radi na omotaču. */}
      <div
        className={classNames(
          layout === "grid" ? "col-span-3" : "",
          disabled ? "pointer-events-none opacity-60" : ""
        )}
      >
        <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
          Vrsta lokacije
          <span className="text-red-500"> *</span>
        </label>
        {/* Kartice idu jedna ispod druge — vrsta se bira jednim pogledom niz popis,
            a opis stane uz naziv umjesto da se lomi u tri retka. */}
        <CardSelect
          options={typeOptions}
          value={values.locationType}
          onChange={handleTypeChange}
          columns={1}
          error={errors.locationType}
        />
      </div>

      {locationRequiresOffice(values.locationType) && (
        <div className={fullWidthClass}>
          <OfficeDropdown
            name="officeId"
            label={config.holderLabel}
            placeholder="Odaberite poslovnicu"
            value={values.officeId}
            onChange={handleOfficeChange}
            error={errors.officeId}
            disabled={disabled}
            required
            width="full"
          />
        </div>
      )}

      {/* Nositelja-kontakt zna pitati i sam tok (npr. "Servis" uz promjenu statusa) — tada
          se ovdje ne prikazuje, ali se vrijednost i dalje sprema uz lokaciju. */}
      {locationRequiresContact(values.locationType) && !hideContact && (
        <div className={fullWidthClass}>
          <ContactDropdown
            name="contactId"
            label={config.holderLabel}
            placeholder={`Odaberite: ${config.holderLabel.toLowerCase()}`}
            value={values.contactId}
            onChange={handleContactChange}
            contactGroupIds={config.contactGroupIds}
            contactType={config.contactType}
            defaultContactType={config.defaultContactType}
            error={errors.contactId}
            disabled={disabled}
            required
            width="full"
          />
        </div>
      )}

      {locationRequiresAddress(values.locationType) && (
        <>
          <div className={wideClass}>
            <TextField
              type="text"
              name="addressLine1"
              label="Adresa"
              placeholder="Ulica i kućni broj"
              value={values.addressLine1}
              onChange={handleTextChange}
              error={errors.addressLine1}
              disabled={disabled}
              required
            />
          </div>
          <div>
            <TextField
              type="text"
              name="addressCity"
              label="Mjesto"
              placeholder="Mjesto"
              value={values.addressCity}
              onChange={handleTextChange}
              error={errors.addressCity}
              disabled={disabled}
            />
          </div>
        </>
      )}

      {showValidFrom && (
        <div>
          <DatePicker
            name="validFrom"
            label="Datum premještaja"
            placeholder="Datum premještaja"
            value={values.validFrom}
            onChange={handleDateChange}
            error={errors.validFrom}
            disabled={disabled}
            width="full"
          />
        </div>
      )}

      {showNote && (
        <div className={wideClass}>
          <TextField
            type="text"
            name="note"
            label="Napomena"
            placeholder="Napomena"
            value={values.note}
            onChange={handleTextChange}
            error={errors.note}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default memo(VehicleLocationFields);
